import { useEffect, useState } from 'react';
import { handleTrack } from '../../../utils/tracker';
import { IRELATED_POSTS } from '../layout/types';

interface ListItem {
  id: string;
  title: string;
  link: string;
}

interface ModifiedData {
  title: string;
  list: ListItem[];
}

const toSnakeCase = (str: string) => {
  return str.replace(/\s+/g, '_').toLowerCase();
};

export const transformArticleData = (
  title: string,
  browseArticleDetails: Array<IRELATED_POSTS> | null
): ModifiedData | [] => {
  if (!browseArticleDetails) {
    return [];
  }
  const modifiedData: ModifiedData = {
    title: title,
    list: [],
  };

  const listData = browseArticleDetails?.map((item) => {
    return {
      id: toSnakeCase(item.title),
      title: item.title,
      link: item.permalink,
    };
  });

  modifiedData.list = listData;

  return modifiedData;
};

export const useScrollDetection = (threshold = 100): boolean => {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > threshold) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  return hasScrolled;
};

export const attachClickTracker = (
  className: string,
  trackLabel: string,
  dom: Document | HTMLElement | null
) => {
  if (dom) {
    const elements = dom.querySelectorAll(`${className}`);
    if (elements.length > 0) {
      const cleanupFunctions: (() => void)[] = [];
      for (const element of Array.from(elements)) {
        if (element instanceof HTMLElement) {
          const handleClick = (e: Event) => {
            let updatedTrackLabel = trackLabel;
            if (element.classList.contains('rc-component') && element.id) {
              updatedTrackLabel = `${trackLabel}_${element.id}`;
            }
            handleTrack({
              label: updatedTrackLabel,
              obj: e.target as HTMLElement,
            });
          };
          element.addEventListener('click', handleClick);
          cleanupFunctions.push(() => {
            element.removeEventListener('click', handleClick);
          });
        }
      }
      return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
      };
    }
  }
  return () => {};
};

type ContentDom = Document | HTMLElement | null;

export const attachTrackers = (
  selectors: string[],
  events: (string | undefined)[],
  content_dom: ContentDom
): (() => void)[] => {
  return selectors.map((selector, index) => {
    const event = events[index];
    if (!event) {
      throw new Error(`Event for selector "${selector}" is undefined.`);
    }
    return attachClickTracker(selector, event, content_dom);
  });
};

export const removeTrackers = (trackers: (() => void)[]): void => {
  trackers.forEach((tracker) => tracker());
};
